// TODO: finish providing types for rigid move manipulator

import * as THREE from 'three';
import App from './application-container';
import { unique } from '@vaaas/fpts/array';
import { Option } from '@vaaas/fpts/option';
import { animate } from './scene';
import {
    uil_get_edges_of_face,
    uil_get_edge_length,
    uil_get_vertices_of_edge,
    uil_get_edge_tangent_at_vertex,
    uil_get_face_geometry,
} from './ui_logic';
import { display_advanced_message_to_user } from './logging';
import { rotationMatrixFromVecTriplets, vectorRotation } from './matrix3';
import { WrappedValue } from '@phi/wrapped-value';
import { rotate_ip } from './geometry';
import * as v3 from './vector3';
import { Edge, Face, Vertex } from '@phi/jcore';
import { bind } from '@vaaas/fpts/iter';
import { pipe } from '@vaaas/fpts/function';

const rigid_colors = {
    cube: 0xaaaaaa,
    ringx: 0x8876e3,
    ringy: 0x8876e3,
    ringz: 0x8876e3,
    planexy: 0x8876e3,
    planeyz: 0x8876e3,
    planexz: 0x8876e3,
    rodx: 0xc74c4c,
    rody: 0x4cc74c,
    rodz: 0x4c4cc7,
    axis_tip: 0xa0a0ff,
    fence_tube: 0x5050ff,
} as const;

const rigid_move_geometry_names = new Set([
    'rigid_move_manip_cube',
    'rigid_move_manip_ringx',
    'rigid_move_manip_ringy',
    'rigid_move_manip_ringy',
    'rigid_move_manip_ringz',
    'rigid_move_manip_vectorx_tip',
    'rigid_move_manip_vectorx_rod',
    'rigid_move_manip_vectory_tip',
    'rigid_move_manip_vectory_rod',
    'rigid_move_manip_vectorz_tip',
    'rigid_move_manip_vectorz_rod',
    'rigid_move_manip_planexy',
    'rigid_move_manip_planeyz',
    'rigid_move_manip_planexz',
    'rigid_move_manip_fence_tube',
    'edge_tangent_manip_vector_tip_1',
    'edge_tangent_manip_vector_tip_2',
    'rigid_move_manip_ringxTrans',
    'rigid_move_manip_ringyTrans',
    'rigid_move_manip_ringyTrans',
    'rigid_move_manip_ringzTrans',
    'rigid_move_manip_vectorx_tipTrans',
    'rigid_move_manip_vectorx_rodTrans',
    'rigid_move_manip_vectory_tipTrans',
    'rigid_move_manip_vectory_rodTrans',
    'rigid_move_manip_vectorz_tipTrans',
    'rigid_move_manip_vectorz_rodTrans',
]);

/** Rigid move manipulator
 *
 * A rigid_move_manipulator geometric description.
 **/
export default class RigidMoveManipulator {
    rigid_move_face_object_id: Face | 0;
    rigid_move_edge_object_ids: Array<Edge>;
    rigid_move_vertex_object_ids: Array<Vertex>;
    is_for_fence: boolean;
    is_for_edge_tangent: boolean;
    required_to_operate: boolean;
    has_already_operated: boolean;
    center: THREE.Vector3;
    local_x_direction: THREE.Vector3;
    local_y_direction: THREE.Vector3;
    local_z_direction: THREE.Vector3;
    vertex_drag_offset: THREE.Vector3;
    sphere_radius: number;
    ring_radius: number;
    cube_size: number;
    axis_vector_size: number;
    plane_size: number;
    fence_tube_size: number;
    cube_material: THREE.MeshBasicMaterial;
    ringx_material: THREE.MeshBasicMaterial;
    ringy_material: THREE.MeshBasicMaterial;
    ringz_material: THREE.MeshBasicMaterial;
    planexy_material: THREE.MeshBasicMaterial;
    planeyz_material: THREE.MeshBasicMaterial;
    planexz_material: THREE.MeshBasicMaterial;
    axis_rodx_material: THREE.MeshBasicMaterial;
    axis_rody_material: THREE.MeshBasicMaterial;
    axis_rodz_material: THREE.MeshBasicMaterial;
    axis_tip_material: THREE.MeshBasicMaterial;
    fence_tube_material: THREE.MeshBasicMaterial;
    cube_geometry: THREE.BoxGeometry;
    ringx_geometry: THREE.TorusGeometry;
    ringy_geometry: THREE.TorusGeometry;
    ringz_geometry: THREE.TorusGeometry;
    axisx_rod_geometry: THREE.CylinderGeometry;
    axisx_tip_geometry: THREE.SphereGeometry;
    axisy_rod_geometry: THREE.CylinderGeometry;
    axisy_tip_geometry: THREE.SphereGeometry;
    axisz_rod_geometry: THREE.CylinderGeometry;
    axisz_tip_geometry: THREE.SphereGeometry;
    planexy_geometry: THREE.PlaneGeometry;
    planeyz_geometry: THREE.PlaneGeometry;
    planexz_geometry: THREE.PlaneGeometry;
    fence_tube_geometry: THREE.CylinderGeometry;
    ringx_cone_geometry: THREE.ConeGeometry;
    ringy_cone_geometry: THREE.ConeGeometry;
    ringz_cone_geometry: THREE.ConeGeometry;
    ringx: THREE.Mesh;
    ringy: THREE.Mesh;
    ringz: THREE.Mesh;
    cube: THREE.Mesh;
    axisx_rod: THREE.Mesh;
    axisx_tip: THREE.Mesh;
    axisy_rod: THREE.Mesh;
    axisy_tip: THREE.Mesh;
    axisz_rod: THREE.Mesh;
    axisz_tip: THREE.Mesh;
    planexy: THREE.Mesh;
    planeyz: THREE.Mesh;
    planexz: THREE.Mesh;
    fence_tube: THREE.Mesh;
    ringx_cone: THREE.Mesh;
    ringy_cone: THREE.Mesh;
    ringz_cone: THREE.Mesh;

    trans_material: THREE.MeshBasicMaterial;

    ringxTrans_geometry: THREE.TorusGeometry;
    ringyTrans_geometry: THREE.TorusGeometry;
    ringzTrans_geometry: THREE.TorusGeometry;
    axisx_rodTrans_geometry: THREE.CylinderGeometry;
    axisx_tipTrans_geometry: THREE.SphereGeometry;
    axisy_rodTrans_geometry: THREE.CylinderGeometry;
    axisy_tipTrans_geometry: THREE.SphereGeometry;
    axisz_rodTrans_geometry: THREE.CylinderGeometry;
    axisz_tipTrans_geometry: THREE.SphereGeometry;

    ringxTrans: THREE.Mesh;
    ringyTrans: THREE.Mesh;
    ringzTrans: THREE.Mesh;
    axisx_rodTrans: THREE.Mesh;
    axisx_tipTrans: THREE.Mesh;
    axisy_rodTrans: THREE.Mesh;
    axisy_tipTrans: THREE.Mesh;
    axisz_rodTrans: THREE.Mesh;
    axisz_tipTrans: THREE.Mesh;

    is_shown: boolean;
    indicated_center_object_id: Option<Vertex | 0>;
    use_local_coordinate_system: boolean;
    rigid_move_initial_hit_ray_origin: THREE.Vector3;
    rigid_move_initial_hit_ray_direction: THREE.Vector3;

    /** Assemble the objects that we move into an array of edges. If only one face was selected, also keep this
     * face id separately because we can extract better geometry information from the face itself than from the edges
     * surrounding it.
     */
    constructor(
        face_object_ids: Array<Face>,
        edge_object_ids: Array<Edge>,
        vertex_object_ids: Array<Vertex>,
        is_for_fence: boolean,
        is_for_edge_tangent: boolean,
        viewSize: number,
        viewsize2vertex_diameter_ratio: number,
    ) {

        // Debug output
        console.log('RODX:' + rigid_colors.rodx);
        console.log('RODY:' + rigid_colors.rody);
        console.log('RODZ:' + rigid_colors.rodz);

        this.has_already_operated = false;
        this.required_to_operate = false;
        this.rigid_move_face_object_id = 0;
        this.rigid_move_edge_object_ids = [];
        this.rigid_move_vertex_object_ids = [];
        this.is_for_fence = is_for_fence;
        this.is_for_edge_tangent = is_for_edge_tangent;

        this.rigid_move_vertex_object_ids = [...vertex_object_ids];

        if (face_object_ids.length == 1 && edge_object_ids.length == 0) {
            // There is only one selected face, and no selected edges
            this.rigid_move_face_object_id = face_object_ids[0]!;
            this.rigid_move_edge_object_ids = uil_get_edges_of_face(this.rigid_move_face_object_id);
        } else if (face_object_ids.length == 0 && edge_object_ids.length == 0) {
            // There are only selected vertices
            //this.rigid_move_vertex_object_ids = [...vertex_object_ids];
        } else
            // There are one or more selected faces / edges
            this.rigid_move_edge_object_ids = unique([
                ...edge_object_ids,
                ...face_object_ids.flatMap(uil_get_edges_of_face),
            ]);

        // Vector description of manipulator location and orientation
        // The manipulator is described by a center, an axis direction and a perpendicular direction.
        // In the default position the center is at 0,0,0, the axis along X and the perpendicular along Y.
        this.center = new THREE.Vector3(0, 0, 0);
        this.local_x_direction = new THREE.Vector3(1, 0, 0);
        this.local_y_direction = new THREE.Vector3(0, 1, 0);
        this.local_z_direction = new THREE.Vector3(0, 0, 1);

        // Choose characteristic dimensions for the manipulator
        this.sphere_radius = viewSize * 8.0 / viewsize2vertex_diameter_ratio;
        this.ring_radius = this.sphere_radius * 1.02;
        this.cube_size = this.sphere_radius / 10.0;
        this.axis_vector_size = this.sphere_radius;
        this.plane_size = this.sphere_radius / 4.0;
        this.fence_tube_size = 0;

        const mobile_factor = App.state.is_mobile ? 5.0 : 0.1;

        // If this manipulator is for fences, see how long the 'tube' should be
        if (this.is_for_fence && this.rigid_move_edge_object_ids.length == 1) {
            const l = uil_get_edge_length(this.rigid_move_edge_object_ids[0]!);
            this.fence_tube_size = l/5.0;
        }

        // An offset vector to be used for dragging vertices clicking off-vertex (i.e. on an axis or plane)
        this.vertex_drag_offset = new THREE.Vector3();

        // Initialize raycasting hit locations to store where it was hit when it started moving.
        this.rigid_move_initial_hit_ray_origin = new THREE.Vector3();
        this.rigid_move_initial_hit_ray_direction = new THREE.Vector3();

        // Manipulator materials
        this.cube_material = new THREE.MeshBasicMaterial({ color: rigid_colors.cube, transparent: false });
        this.ringx_material = new THREE.MeshBasicMaterial({ color: rigid_colors.ringx, transparent: false });
        this.ringy_material = new THREE.MeshBasicMaterial({ color: rigid_colors.ringy, transparent: false });
        this.ringz_material = new THREE.MeshBasicMaterial({ color: rigid_colors.ringz, transparent: false });
        this.planexy_material = new THREE.MeshBasicMaterial({ color: rigid_colors.planexy, side: THREE.DoubleSide });
        this.planeyz_material = new THREE.MeshBasicMaterial({ color: rigid_colors.planeyz, side: THREE.DoubleSide });
        this.planexz_material = new THREE.MeshBasicMaterial({ color: rigid_colors.planexz, side: THREE.DoubleSide });
        this.axis_rodx_material = new THREE.MeshBasicMaterial({ color: rigid_colors.rodx, transparent: false, opacity: 1 });
        this.axis_rody_material = new THREE.MeshBasicMaterial({ color: rigid_colors.rody, transparent: false, opacity: 1 });
        this.axis_rodz_material = new THREE.MeshBasicMaterial({ color: rigid_colors.rodz, transparent: false, opacity: 1 });
        this.axis_tip_material = new THREE.MeshBasicMaterial({ color: rigid_colors.axis_tip });
        this.fence_tube_material = new THREE.MeshBasicMaterial({ color: rigid_colors.fence_tube });

        const rod_radius = this.axis_vector_size / 80.0;
        const tip_radius = rod_radius * 3.0;

        // Manipulator geometries
        this.cube_geometry = new THREE.BoxGeometry(this.cube_size, this.cube_size, this.cube_size, 20, 1);
        if (!this.is_for_fence)
            this.ringx_geometry = new THREE.TorusGeometry(this.ring_radius, tip_radius/3.0, 32, 32, Math.PI/2.0 - Math.PI/10.0);
        else
            this.ringx_geometry = new THREE.TorusGeometry(this.ring_radius/2.0, tip_radius, 32, 32, Math.PI/4.0);
        this.ringy_geometry = new THREE.TorusGeometry(this.ring_radius, tip_radius/3.0, 32, 32, Math.PI/2.0 - Math.PI/10.0);
        this.ringz_geometry = new THREE.TorusGeometry(this.ring_radius, tip_radius/3.0, 32, 32, Math.PI/2.0 - Math.PI/10.0);
        this.axisx_rod_geometry = new THREE.CylinderGeometry(rod_radius*1.5, rod_radius*1.5, this.axis_vector_size, 32, 1, false);
        this.axisx_tip_geometry = new THREE.SphereGeometry(tip_radius, 32, 32);
        if (!this.is_for_fence)
            this.axisy_rod_geometry = new THREE.CylinderGeometry(rod_radius*1.5, rod_radius*1.5, this.axis_vector_size, 32, 1, false);
        else
            this.axisy_rod_geometry = new THREE.CylinderGeometry(rod_radius*1.5, rod_radius*1.5, this.ring_radius/2.0, 32, 1, false);
        this.axisy_tip_geometry = new THREE.SphereGeometry(tip_radius, 32, 32);
        this.axisz_rod_geometry = new THREE.CylinderGeometry(rod_radius*1.5, rod_radius*1.5, this.axis_vector_size, 32, 1, false);
        this.axisz_tip_geometry = new THREE.SphereGeometry(tip_radius, 32, 32);
        this.planexy_geometry = new THREE.PlaneGeometry(this.plane_size, this.plane_size);
        this.planeyz_geometry = new THREE.PlaneGeometry(this.plane_size, this.plane_size);
        this.planexz_geometry = new THREE.PlaneGeometry(this.plane_size, this.plane_size);
        this.fence_tube_geometry = new THREE.CylinderGeometry(tip_radius, tip_radius, this.fence_tube_size * 0.9, 32, 1, false);

        // Add cones like arrows for ring direction
        this.ringx_cone_geometry = new THREE.ConeGeometry(tip_radius * 0.7 * 1.2, tip_radius, 32);
        this.ringy_cone_geometry = new THREE.ConeGeometry(tip_radius * 0.7 * 1.2, tip_radius, 32);
        this.ringz_cone_geometry = new THREE.ConeGeometry(tip_radius * 0.7 * 1.2, tip_radius, 32);

        // Transparent geometries
        this.trans_material = new THREE.MeshBasicMaterial({
            color: rigid_colors.cube,
            transparent: true,
            opacity: 0.0,
            depthTest: false,
            depthWrite: false
        });

        if (!this.is_for_fence)
            this.ringxTrans_geometry = new THREE.TorusGeometry(this.ring_radius, tip_radius/3.0 * 2.0*mobile_factor, 32, 32, Math.PI/2.0 - Math.PI/10.0);
        else
            this.ringxTrans_geometry = new THREE.TorusGeometry(this.ring_radius/2.0, tip_radius * 2.0*mobile_factor, 32, 32, Math.PI/4.0);
        this.ringyTrans_geometry = new THREE.TorusGeometry(this.ring_radius, tip_radius/3.0 * 2.0*mobile_factor, 32, 32, Math.PI/2.0 - Math.PI/10.0);
        this.ringzTrans_geometry = new THREE.TorusGeometry(this.ring_radius, tip_radius/3.0 * 2.0*mobile_factor, 32, 32, Math.PI/2.0 - Math.PI/10.0);
        this.axisx_rodTrans_geometry = new THREE.CylinderGeometry(rod_radius*1.5 * mobile_factor, rod_radius*1.5 * mobile_factor, this.axis_vector_size, 32, 1, false);
        this.axisx_tipTrans_geometry = new THREE.SphereGeometry(tip_radius * mobile_factor, 32, 32);
        if (!this.is_for_fence)
            this.axisy_rodTrans_geometry = new THREE.CylinderGeometry(rod_radius*1.5 * mobile_factor, rod_radius*1.5 * mobile_factor, this.axis_vector_size, 32, 1, false);
        else
            this.axisy_rodTrans_geometry = new THREE.CylinderGeometry(rod_radius*1.5 * mobile_factor, rod_radius*1.5 * mobile_factor, this.ring_radius/2.0, 32, 1, false);
        this.axisy_tipTrans_geometry = new THREE.SphereGeometry(tip_radius * mobile_factor, 32, 32);
        this.axisz_rodTrans_geometry = new THREE.CylinderGeometry(rod_radius*1.5 * mobile_factor, rod_radius*1.5 * mobile_factor, this.axis_vector_size, 32, 1, false);
        this.axisz_tipTrans_geometry = new THREE.SphereGeometry(tip_radius * mobile_factor, 32, 32);

        // Rotate geometries
        const vx = new THREE.Vector3(1, 0, 0);
        const vy = new THREE.Vector3(0, 1, 0);
        const vz = new THREE.Vector3(0, 0, 1);
        rotate_ip(vx, this.cube_geometry);
        rotate_ip(vx, this.axisx_rod_geometry);
        rotate_ip(vy, this.axisy_rod_geometry);
        rotate_ip(vz, this.axisz_rod_geometry);
        rotate_ip(vx, this.fence_tube_geometry);
        rotate_ip(vx, this.axisx_rodTrans_geometry);
        rotate_ip(vy, this.axisy_rodTrans_geometry);
        rotate_ip(vz, this.axisz_rodTrans_geometry);

        this.ringx_geometry.rotateY(- Math.PI / 2.0);
        this.ringy_geometry.rotateX(Math.PI / 2.0);
        if (!this.is_for_fence)
            this.ringx_geometry.rotateX(-Math.PI/18.0);
        else
            this.ringx_geometry.rotateX(-3*Math.PI/8.0);
        this.ringy_geometry.rotateY(-Math.PI/18.0);
        this.ringz_geometry.rotateZ(Math.PI/18.0);

        this.ringxTrans_geometry.rotateY(- Math.PI / 2.0);
        this.ringyTrans_geometry.rotateX(Math.PI / 2.0);
        if (!this.is_for_fence)
            this.ringxTrans_geometry.rotateX(-Math.PI/18.0);
        else
            this.ringxTrans_geometry.rotateX(-3*Math.PI/8.0);
        this.ringyTrans_geometry.rotateY(-Math.PI/18.0);
        this.ringzTrans_geometry.rotateZ(Math.PI/18.0);

        this.planeyz_geometry.rotateY(Math.PI / 2.0);
        this.planexz_geometry.rotateX(Math.PI / 2.0);

        // Translate geometries
        this.axisx_rod_geometry.translate(this.axis_vector_size / 2 + this.cube_size /1.5, 0, 0);
        this.axisx_tip_geometry.translate(this.axis_vector_size*1.1 + this.cube_size /1.5, 0, 0);
        if (!this.is_for_fence)
            this.axisy_rod_geometry.translate(0, this.axis_vector_size / 2 + this.cube_size /1.5, 0);
        else
            this.axisy_rod_geometry.translate(0, this.ring_radius / 2 / 2, 0);
        this.axisy_tip_geometry.translate(0, this.axis_vector_size*1.1 + this.cube_size /1.5, 0);
        this.axisz_rod_geometry.translate(0, 0, this.axis_vector_size / 2 + this.cube_size /1.5);
        this.axisz_tip_geometry.translate(0, 0, this.axis_vector_size*1.1 + this.cube_size /1.5);

        this.axisx_rodTrans_geometry.translate(this.axis_vector_size / 2 + this.cube_size /1.5, 0, 0);
        this.axisx_tipTrans_geometry.translate(this.axis_vector_size*1.1 + this.cube_size /1.5, 0, 0);
        if (!this.is_for_fence)
            this.axisy_rodTrans_geometry.translate(0, this.axis_vector_size / 2 + this.cube_size /1.5, 0);
        else
            this.axisy_rodTrans_geometry.translate(0, this.ring_radius / 2 / 2, 0);
        this.axisy_tipTrans_geometry.translate(0, this.axis_vector_size*1.1 + this.cube_size /1.5, 0);
        this.axisz_rodTrans_geometry.translate(0, 0, this.axis_vector_size / 2 + this.cube_size /1.5);
        this.axisz_tipTrans_geometry.translate(0, 0, this.axis_vector_size*1.1 + this.cube_size /1.5);

        this.planexy_geometry.translate(0.7*this.plane_size, 0.7*this.plane_size, 0.01*this.plane_size);
        this.planeyz_geometry.translate(0.01*this.plane_size, 0.7*this.plane_size, 0.7*this.plane_size);
        this.planexz_geometry.translate(0.7*this.plane_size, 0.01*this.plane_size, 0.7*this.plane_size);

        // Take care of ring cones
        this.ringx_cone_geometry.rotateX(Math.PI / 2.0);
        this.ringx_cone_geometry.translate(0, this.ring_radius, 0);
        this.ringx_cone_geometry.rotateX(Math.PI / 2.0 * 0.9);

        this.ringy_cone_geometry.rotateZ(-Math.PI / 2.0);
        this.ringy_cone_geometry.translate(0, 0, this.ring_radius);
        this.ringy_cone_geometry.rotateY(Math.PI / 2.0 * 0.9);

        this.ringz_cone_geometry.translate(this.ring_radius, 0, 0);
        this.ringz_cone_geometry.rotateZ(Math.PI / 2.0 * 0.9);

        // Geometry names
        this.cube_geometry.name = 'rigid_move_manip_cube';
        if (!this.is_for_fence)
            this.ringx_geometry.name = 'rigid_move_manip_ringx';
        else
            this.ringx_geometry.name = 'rigid_move_manip_fence_ring';
        this.ringy_geometry.name = 'rigid_move_manip_ringy';
        this.ringz_geometry.name = 'rigid_move_manip_ringz';
        this.axisx_rod_geometry.name = 'rigid_move_manip_vectorx_rod';
        this.axisx_tip_geometry.name = 'rigid_move_manip_vectorx_tip';
        if (!this.is_for_fence)
            this.axisy_rod_geometry.name = 'rigid_move_manip_vectory_rod';
        else
            this.axisy_rod_geometry.name = 'rigid_move_manip_fence_normal';
        this.axisy_tip_geometry.name = 'rigid_move_manip_vectory_tip';
        this.axisz_rod_geometry.name = 'rigid_move_manip_vectorz_rod';
        this.axisz_tip_geometry.name = 'rigid_move_manip_vectorz_tip';
        this.planexy_geometry.name = 'rigid_move_manip_planexy';
        this.planeyz_geometry.name = 'rigid_move_manip_planeyz';
        this.planexz_geometry.name = 'rigid_move_manip_planexz';
        this.fence_tube_geometry.name = 'rigid_move_manip_fence_tube';

        // Transparent geometry names
        if (!this.is_for_fence)
            this.ringxTrans_geometry.name = 'rigid_move_manip_ringxTrans';
        else
            this.ringxTrans_geometry.name = 'rigid_move_manip_fence_ringTrans';
        this.ringyTrans_geometry.name = 'rigid_move_manip_ringyTrans';
        this.ringzTrans_geometry.name = 'rigid_move_manip_ringzTrans';
        this.axisx_rodTrans_geometry.name = 'rigid_move_manip_vectorx_rodTrans';
        this.axisx_tipTrans_geometry.name = 'rigid_move_manip_vectorx_tipTrans';
        if (!this.is_for_fence)
            this.axisy_rodTrans_geometry.name = 'rigid_move_manip_vectory_rodTrans';
        else
            this.axisy_rodTrans_geometry.name = 'rigid_move_manip_fence_normalTrans';
        this.axisy_tipTrans_geometry.name = 'rigid_move_manip_vectory_tipTrans';
        this.axisz_rodTrans_geometry.name = 'rigid_move_manip_vectorz_rodTrans';
        this.axisz_tipTrans_geometry.name = 'rigid_move_manip_vectorz_tipTrans';

        // Manipulator elements
        this.ringx = new THREE.Mesh(this.ringx_geometry, this.ringx_material);
        this.ringy = new THREE.Mesh(this.ringy_geometry, this.ringy_material);
        this.ringz = new THREE.Mesh(this.ringz_geometry, this.ringz_material);
        this.cube = new THREE.Mesh(this.cube_geometry, this.cube_material);
        this.axisx_rod = new THREE.Mesh(this.axisx_rod_geometry, this.axis_rodx_material);
        this.axisx_tip = new THREE.Mesh(this.axisx_tip_geometry, this.axis_tip_material);
        this.axisy_rod = new THREE.Mesh(this.axisy_rod_geometry, this.axis_rody_material);
        this.axisy_tip = new THREE.Mesh(this.axisy_tip_geometry, this.axis_tip_material);
        this.axisz_rod = new THREE.Mesh(this.axisz_rod_geometry, this.axis_rodz_material);
        this.axisz_tip = new THREE.Mesh(this.axisz_tip_geometry, this.axis_tip_material);
        this.planexy = new THREE.Mesh(this.planexy_geometry, this.planexy_material);
        this.planexz = new THREE.Mesh(this.planexz_geometry, this.planexz_material);
        this.planeyz = new THREE.Mesh(this.planeyz_geometry, this.planeyz_material);
        this.fence_tube = new THREE.Mesh(this.fence_tube_geometry, this.fence_tube_material);
        this.ringx_cone = new THREE.Mesh(this.ringx_cone_geometry, this.ringx_material);
        this.ringy_cone = new THREE.Mesh(this.ringy_cone_geometry, this.ringy_material);
        this.ringz_cone = new THREE.Mesh(this.ringz_cone_geometry, this.ringz_material);

        // Transparent manipulator elements
        this.ringxTrans = new THREE.Mesh(this.ringxTrans_geometry, this.trans_material);
        this.ringyTrans = new THREE.Mesh(this.ringyTrans_geometry, this.trans_material);
        this.ringzTrans = new THREE.Mesh(this.ringzTrans_geometry, this.trans_material);
        this.axisx_rodTrans = new THREE.Mesh(this.axisx_rodTrans_geometry, this.trans_material);
        this.axisx_tipTrans = new THREE.Mesh(this.axisx_tipTrans_geometry, this.trans_material);
        this.axisy_rodTrans = new THREE.Mesh(this.axisy_rodTrans_geometry, this.trans_material);
        this.axisy_tipTrans = new THREE.Mesh(this.axisy_tipTrans_geometry, this.trans_material);
        this.axisz_rodTrans = new THREE.Mesh(this.axisz_rodTrans_geometry, this.trans_material);
        this.axisz_tipTrans = new THREE.Mesh(this.axisz_tipTrans_geometry, this.trans_material);

        // Flag for shown/not shown
        this.is_shown = false;

        // See if a specific center vertex has been indicated
        this.indicated_center_object_id = 0;
        if (this.rigid_move_vertex_object_ids.length == 1 || (
            this.rigid_move_vertex_object_ids.length > 1
            && this.rigid_move_edge_object_ids.length == 0
            && this.rigid_move_face_object_id == 0
        )) {
            this.indicated_center_object_id = this.rigid_move_vertex_object_ids[0]!;
        }

        // Add to respective scene nodes
        if (this.is_for_fence) {
            uid_manip_raynode.add(this.ringx);
            uid_manip_raynode.add(this.axisy_rod);
            uid_manip_raynode.add(this.fence_tube);
        } else {
            uid_manip_raynode.add(this.ringx);
            uid_manip_raynode.add(this.ringy);
            uid_manip_raynode.add(this.ringz);
            uid_manip_raynode.add(this.cube);
            uid_manip_raynode.add(this.axisx_rod);
            uid_manip_raynode.add(this.axisx_tip);
            uid_manip_raynode.add(this.axisy_rod);
            uid_manip_raynode.add(this.axisy_tip);
            uid_manip_raynode.add(this.axisz_rod);
            uid_manip_raynode.add(this.axisz_tip);
            uid_manip_raynode.add(this.planexy);
            uid_manip_raynode.add(this.planeyz);
            uid_manip_raynode.add(this.planexz);
            uid_manip_raynode.add(this.ringx_cone);
            uid_manip_raynode.add(this.ringy_cone);
            uid_manip_raynode.add(this.ringz_cone);

            uid_manip_raynode.add(this.ringxTrans);
            uid_manip_raynode.add(this.ringyTrans);
            uid_manip_raynode.add(this.ringzTrans);
            uid_manip_raynode.add(this.axisx_rodTrans);
            uid_manip_raynode.add(this.axisx_tipTrans);
            uid_manip_raynode.add(this.axisy_rodTrans);
            uid_manip_raynode.add(this.axisy_tipTrans);
            uid_manip_raynode.add(this.axisz_rodTrans);
            uid_manip_raynode.add(this.axisz_tipTrans);
        }

        // Indicate whether it is attached to local coordinate system
        this.use_local_coordinate_system = false;
    }

    display(use_local_coordinate_system = false, use_prestored_tmp_placement = false) {
        this.attach_to_objects(this.is_for_fence || use_local_coordinate_system, 0.5, use_prestored_tmp_placement);
        this.is_shown = true;
        if (App.state.editing_edge_tangent_mode && uid_selected_edges.length == 1 && uid_selected_vertices.length == 1 && uid_selected_faces.length == 0)
            display_advanced_message_to_user('rigidMoveManipulatorTangentMode');
        else
            display_advanced_message_to_user('rigidMoveManipulator');
        animate();
    }

    remove() {
        uid_rigid_move_manipulator = null;

        uid_manip_raynode.remove(this.ringx);
        uid_manip_raynode.remove(this.ringy);
        uid_manip_raynode.remove(this.ringz);
        uid_manip_raynode.remove(this.cube);
        uid_manip_raynode.remove(this.axisx_rod);
        uid_manip_raynode.remove(this.axisx_tip);
        uid_manip_raynode.remove(this.axisy_rod);
        uid_manip_raynode.remove(this.axisy_tip);
        uid_manip_raynode.remove(this.axisz_rod);
        uid_manip_raynode.remove(this.axisz_tip);
        uid_manip_raynode.remove(this.planexy);
        uid_manip_raynode.remove(this.planeyz);
        uid_manip_raynode.remove(this.planexz);
        uid_manip_raynode.remove(this.ringx_cone);
        uid_manip_raynode.remove(this.ringy_cone);
        uid_manip_raynode.remove(this.ringz_cone);
        uid_manip_raynode.remove(this.fence_tube);
        uid_manip_raynode.remove(this.ringxTrans);
        uid_manip_raynode.remove(this.ringyTrans);
        uid_manip_raynode.remove(this.ringzTrans);
        uid_manip_raynode.remove(this.axisx_rodTrans);
        uid_manip_raynode.remove(this.axisx_tipTrans);
        uid_manip_raynode.remove(this.axisy_rodTrans);
        uid_manip_raynode.remove(this.axisy_tipTrans);
        uid_manip_raynode.remove(this.axisz_rodTrans);
        uid_manip_raynode.remove(this.axisz_tipTrans);

        // Manipulator elements
        this.cube.geometry.dispose();
        (this.cube.material as THREE.Material).dispose();
        this.ringx.geometry.dispose();
        (this.ringx.material as THREE.Material).dispose();
        this.ringy.geometry.dispose();
        (this.ringy.material as THREE.Material).dispose();
        this.ringz.geometry.dispose();
        (this.ringz.material as THREE.Material).dispose();
        this.axisx_rod.geometry.dispose();
        (this.axisx_rod.material as THREE.Material).dispose();
        this.axisx_tip.geometry.dispose();
        (this.axisx_tip.material as THREE.Material).dispose();
        this.axisy_rod.geometry.dispose();
        (this.axisy_rod.material as THREE.Material).dispose();
        this.axisy_tip.geometry.dispose();
        (this.axisy_tip.material as THREE.Material).dispose();
        this.axisz_rod.geometry.dispose();
        (this.axisz_rod.material as THREE.Material).dispose();
        this.axisz_tip.geometry.dispose();
        (this.axisz_tip.material as THREE.Material).dispose();
        this.planexy.geometry.dispose();
        (this.planexy.material as THREE.Material).dispose();
        this.planeyz.geometry.dispose();
        (this.planeyz.material as THREE.Material).dispose();
        this.planexz.geometry.dispose();
        (this.planexz.material as THREE.Material).dispose();
        this.ringx_cone.geometry.dispose();
        (this.ringx_cone.material as THREE.Material).dispose();
        this.ringy_cone.geometry.dispose();
        (this.ringy_cone.material as THREE.Material).dispose();
        this.ringz_cone.geometry.dispose();
        (this.ringz_cone.material as THREE.Material).dispose();
        this.fence_tube.geometry.dispose();
        (this.fence_tube.material as THREE.Material).dispose();

        this.ringxTrans.geometry.dispose();
        this.ringyTrans.geometry.dispose();
        this.ringzTrans.geometry.dispose();
        this.axisx_rodTrans.geometry.dispose();
        this.axisx_tipTrans.geometry.dispose();
        this.axisy_rodTrans.geometry.dispose();
        this.axisy_tipTrans.geometry.dispose();
        this.axisz_rodTrans.geometry.dispose();
        this.axisz_tipTrans.geometry.dispose();
        (this.ringxTrans.material as THREE.Material).dispose(); // It's the same material for all.

        this.is_shown = false;
    }

    attach_to_objects(use_local_coordinate_system: boolean, edge_parameter = 0.5, use_prestored_tmp_placement = false) {
        let objects_center = new THREE.Vector3();
        let objects_axis_direction = new THREE.Vector3();
        let objects_perpendicular_direction = new THREE.Vector3();

        if (use_prestored_tmp_placement) {
            objects_center.copy(tmp_rigid_move_manipulator_center);
            objects_axis_direction.copy(tmp_rigid_move_manipulator_axis);
            objects_perpendicular_direction.copy(tmp_rigid_move_manipulator_normal);
            this.match_to_vectors(objects_center, objects_axis_direction, objects_perpendicular_direction);
            return;
        }

        this.use_local_coordinate_system = use_local_coordinate_system;

        // Get edge list or face geometry
        const wv_radius = new WrappedValue(0);
        const wv_flatness = new WrappedValue(0);

        if (this.is_for_edge_tangent) {
            // Rotating tangent of edge at vertex.
            objects_center.copy(uid_vertices.getItem(this.rigid_move_vertex_object_ids[0]!)!.object3d!.position);
            objects_axis_direction.copy(uil_get_edge_tangent_at_vertex(this.rigid_move_edge_object_ids[0]!, this.rigid_move_vertex_object_ids[0]!).normalize());
            // Check whether we may need to invert the direction of the tangent by comparing it with a vector going from this vertex to
            // the opposite vertex of the edge.
            const vs = uil_get_vertices_of_edge(this.rigid_move_edge_object_ids[0]!);
            const v_remote = vs[0] == this.rigid_move_vertex_object_ids[0]! ? vs[1] : vs[0];
            const v_edge = new THREE.Vector3().subVectors(
                uid_vertices.getItem(v_remote)!.object3d!.position,
                uid_vertices.getItem(this.rigid_move_vertex_object_ids[0]!)!.object3d!.position
            ).normalize();
            objects_axis_direction = v_edge.dot(objects_axis_direction) < 0 ? objects_axis_direction :
                objects_axis_direction.multiplyScalar(-1);
            // Let perpendicular be like below, with a cross product
            objects_perpendicular_direction = v3.find_best_orthogonal_direction_for_vector(objects_axis_direction);
            this.match_to_vectors(objects_center, objects_axis_direction, objects_perpendicular_direction);
            return;
        } else if (!this.rigid_move_face_object_id && this.rigid_move_edge_object_ids.length == 1) {
            // Moving just one edge. Either rigidly, or moving its fence.
            // @ts-ignore
            uil_get_edge_list_geometry(this.rigid_move_edge_object_ids, false, objects_center, objects_axis_direction, wv_radius, objects_perpendicular_direction);
            // Let objects_center be the middle of the edge
            // @ts-ignore
            objects_center = uil_get_point_at_edge_param(this.rigid_move_edge_object_ids[0], edge_parameter);

            if (this.is_for_fence) {
                // Moving fence. Let axis_direction be the tangent to the edge at this parameter value.
                // @ts-ignore
                objects_axis_direction = uil_get_tangent_at_edge_param(this.rigid_move_edge_object_ids[0], edge_parameter);
                objects_axis_direction.normalize();
                // Let the perpendicular be the normal to the face
                // @ts-ignore
                objects_perpendicular_direction = find_edge_normal(this.rigid_move_edge_object_ids[0], objects_center, uid_raycaster.ray.direction);
            } else {
                // Moving edge rigidly. Let axis_direction be the edge normal at the center
                // @ts-ignore
                objects_axis_direction = find_edge_normal(this.rigid_move_edge_object_ids[0], objects_center, uid_raycaster.ray.direction);
                // Let perpendicular be like below, with a cross product
                const n = new THREE.Vector3();
                n.x = 0.316227766;
                n.y = 0.948683298;
                n.z = 0.572665981;
                objects_perpendicular_direction.crossVectors(objects_axis_direction, n).normalize();
            }

            wv_radius.v = uil_get_edge_length(this.rigid_move_edge_object_ids[0]!);
        } else if (!this.rigid_move_face_object_id && this.rigid_move_edge_object_ids.length > 1) {
            if (this.rigid_move_edge_object_ids.length < 100) {
                // For a modest number of edges, use the core get_edge_list_geometry function
                const edges = Array.from(this.rigid_move_edge_object_ids);
                // @ts-ignore
                uil_get_edge_list_geometry(edges, false, objects_center, objects_axis_direction, wv_radius, objects_perpendicular_direction);
            } else {
                // For a large number of edges, use their vertices to find the object center
                const edge_vertices = pipe(
                    this.rigid_move_edge_object_ids,
                    bind(uil_get_vertices_of_edge),
                    unique,
                );
                objects_center.set(0, 0, 0);
                edge_vertices
                    .filter(x => uid_vertices.hasItem(x))
                    .reduce((oc, vid) => oc.add(uid_vertices.getItem(vid)!.object3d!.position), objects_center);
                objects_center.multiplyScalar(1.0/edge_vertices.length);
                objects_axis_direction.set(1, 0, 0);
                objects_perpendicular_direction.set(0, 1, 0);
                wv_radius.v = 1.0;
            }
        } else if (this.rigid_move_face_object_id)
            // @ts-ignore
            uil_get_face_geometry(this.rigid_move_face_object_id, objects_center, objects_axis_direction, wv_radius, objects_perpendicular_direction, wv_flatness);
        else {
            // No face or edges were selected, we're moving just one (or more than one?) vertex.
            // Get the geometry from the vertex normal
            const n = new THREE.Vector3();
            n.x = 0.316227766;
            n.y = 0.948683298;
            n.z = 0.572665981;
            if (!this.indicated_center_object_id)
                this.indicated_center_object_id = this.rigid_move_vertex_object_ids[0];
            // @ts-ignore
            const v = find_vertex_normal(this.indicated_center_object_id, this.indicated_center_object_id, n);
            objects_axis_direction.copy(v);
            objects_perpendicular_direction.crossVectors(objects_axis_direction, n).normalize();
        }

        // If a vertex was indicated as a center, use it instead.
        if (this.indicated_center_object_id != 0) {
            const v = uid_vertices.getItem(this.indicated_center_object_id!);
            objects_center.x = v!.object3d!.position.x;
            objects_center.y = v!.object3d!.position.y;
            objects_center.z = v!.object3d!.position.z;
        }

        if (use_local_coordinate_system) {
            if (!this.is_for_fence) {
                // Adjust local coordinate system so it falls close to global planes
                const vO = new THREE.Vector3(0, 0, 0);
                const vx = new THREE.Vector3(1, 0, 0);
                const vy = new THREE.Vector3(0, 1, 0);
                const vz = new THREE.Vector3(0, 0, 1);
                const ax = Math.abs(objects_axis_direction.dot(vx));
                const ay = Math.abs(objects_axis_direction.dot(vy));
                const az = Math.abs(objects_axis_direction.dot(vz));
                if (ax >= ay && ax >= az) {
                    // Rotate secondary vector onto x-y plane
                    const n = new THREE.Vector3();
                    n.crossVectors(objects_axis_direction, vy).normalize();
                    objects_perpendicular_direction.copy(objects_axis_direction);
                    const angle = Math.PI / 2;
                    objects_perpendicular_direction.applyAxisAngle(n, angle);
                } else if (ay >= ax && ay >= az) {
                    // Rotate secondary vector onto y-z plane
                    const n = new THREE.Vector3();
                    n.crossVectors(objects_axis_direction, vz).normalize();
                    objects_perpendicular_direction.copy(objects_axis_direction);
                    const angle = Math.PI / 2;
                    objects_perpendicular_direction.applyAxisAngle(n, angle);
                } else {
                    // Rotate secondary vector onto z-x plane
                    const n = new THREE.Vector3();
                    n.crossVectors(objects_axis_direction, vx).normalize();
                    objects_perpendicular_direction.copy(objects_axis_direction);
                    const angle = Math.PI / 2;
                    objects_perpendicular_direction.applyAxisAngle(n, angle);
                }
            }

            objects_perpendicular_direction.normalize();
            this.match_to_vectors(objects_center, objects_axis_direction, objects_perpendicular_direction);
        } else {
            // Use global orthogonal axes, but adjusted so that they don't fall within the object.
            const vO = new THREE.Vector3(0, 0, 0);
            const vx = new THREE.Vector3(1, 0, 0);
            const vy = new THREE.Vector3(0, 1, 0);
            const vz = new THREE.Vector3(0, 0, 1);
            let ax = Math.abs(objects_axis_direction.dot(vx));
            let ay = Math.abs(objects_axis_direction.dot(vy));
            let az = Math.abs(objects_axis_direction.dot(vz));
            if (ax >= ay && ax >= az) {
                // Perpendicular is closest to the X or -X axis
                ax = objects_axis_direction.dot(vx);
                const aminusx = objects_axis_direction.dot(vx.multiplyScalar(-1));
                if (ax > aminusx) {
                    objects_axis_direction.set(1, 0, 0);
                    objects_perpendicular_direction.set(0, 1, 0);
                } else {
                    objects_axis_direction.set(-1, 0, 0);
                    objects_perpendicular_direction.set(0, -1, 0);
                }
            } else if (ay >= ax && ay >= az) {
                // Perpendicular is closest to the Y or -Y axis
                ay = objects_axis_direction.dot(vy);
                const aminusy = objects_axis_direction.dot(vy.multiplyScalar(-1));
                if (ay > aminusy) {
                    objects_axis_direction.set(0, 1, 0);
                    objects_perpendicular_direction.set(0, 0, 1);
                } else {
                    objects_axis_direction.set(0, -1, 0);
                    objects_perpendicular_direction.set(0, 0, -1);
                }
            } else {
                // Perpendicular is closest to the Z or -Z axis
                az = objects_axis_direction.dot(vz);
                const aminusz = objects_axis_direction.dot(vz.multiplyScalar(-1));
                if (az > aminusz) {
                    objects_axis_direction.set(0, 0, 1);
                    objects_perpendicular_direction.set(1, 0, 0);
                } else {
                    objects_axis_direction.set(0, 0, -1);
                    objects_perpendicular_direction.set(-1, 0, 0);
                }
            }

            this.match_to_vectors(objects_center, objects_axis_direction, objects_perpendicular_direction);
        }
    }

    match_to_vectors(center: THREE.Vector3, axis: THREE.Vector3, normal: THREE.Vector3) {
        // Takes a manipulator and transforms it so that it matches the triplet (center, axis, normal).

        /*
        console_log('Center: ', center);
        console_log('Axis: ', axis);
        console_log('Normal: ', normal);
        console_log('Local center: ', this.center);
        console_log('Local X: ', this.local_x_direction);
        console_log('Local Y: ', this.local_y_direction);
        console_log('Local Z: ', this.local_z_direction);
        */

        // Normalize input vectors
        const va = new THREE.Vector3(this.local_x_direction.x, this.local_x_direction.y, this.local_x_direction.z).normalize();
        const vb = new THREE.Vector3(this.local_y_direction.x, this.local_y_direction.y, this.local_y_direction.z).normalize();

        // Create the third vector from the manipulator vectors by crossing them
        const vc = new THREE.Vector3();
        vc.crossVectors(this.local_x_direction, this.local_y_direction);

        //console_log('vc: ', vc);

        const vd = new THREE.Vector3(axis.x, axis.y, axis.z).normalize();
        const ve = new THREE.Vector3(normal.x, normal.y, normal.z).normalize();
        // Create the third vector from the axis and normal by crossing them
        const vf = new THREE.Vector3();
        vf.crossVectors(axis, normal);

        /*console_log('Local X: ', this.local_x_direction);
        console_log('Local Y: ', this.local_y_direction);
        console_log('vb: ', vb);
        console_log('vc: ', vc);
        console_log('vd: ', vd);
        console_log('ve: ', ve);
        console_log('vf: ', vf);*/

        // Get transform that brings manipulator onto (axis, normal)
        const R = rotationMatrixFromVecTriplets(va, vb, vc, vd, ve, vf);

        const tocenter = new THREE.Vector3();
        const andback = new THREE.Vector3();
        tocenter.copy(this.center).multiplyScalar(-1);
        andback.copy(this.center);

        // Translate to origin
        this.translate(tocenter);
        // Rotate
        this.rotate(R);
        // Translate back
        this.translate(andback);

        // Translate manipulator
        const t = new THREE.Vector3();
        t.subVectors(center, this.center);
        this.translate(t);
        //console_log('t:', t);
        //console_log('New center:', this.center);
    }

    rotate(R: THREE.Matrix3) {
        // Rotate manipulator by matrix 'R'

        // Rotate main description vectors.
        this.local_x_direction.applyMatrix3(R);
        this.local_y_direction.applyMatrix3(R);
        this.local_z_direction.applyMatrix3(R);

        // Apply X products to adjust matrices onto orthogonal triplet
        this.local_z_direction.crossVectors(this.local_x_direction, this.local_y_direction).normalize();
        this.local_y_direction.crossVectors(this.local_z_direction, this.local_x_direction).normalize();

        //console_log('Local X: ', this.local_x_direction);
        //console_log('Local Y: ', this.local_y_direction);

        // Rotate geometries.
        const m4 = new THREE.Matrix4();
        m4.set(
            R.elements[0]!, R.elements[3]!, R.elements[6]!, 0,
            R.elements[1]!, R.elements[4]!, R.elements[7]!, 0,
            R.elements[2]!, R.elements[5]!, R.elements[8]!, 0,
            0, 0, 0, 1,
        );

        this.cube_geometry.applyMatrix4(m4);
        this.ringx_geometry.applyMatrix4(m4);
        this.ringy_geometry.applyMatrix4(m4);
        this.ringz_geometry.applyMatrix4(m4);
        this.axisx_rod_geometry.applyMatrix4(m4);
        this.axisx_tip_geometry.applyMatrix4(m4);
        this.axisy_rod_geometry.applyMatrix4(m4);
        this.axisy_tip_geometry.applyMatrix4(m4);
        this.axisz_rod_geometry.applyMatrix4(m4);
        this.axisz_tip_geometry.applyMatrix4(m4);
        this.planexy_geometry.applyMatrix4(m4);
        this.planeyz_geometry.applyMatrix4(m4);
        this.planexz_geometry.applyMatrix4(m4);
        this.ringx_cone_geometry.applyMatrix4(m4);
        this.ringy_cone_geometry.applyMatrix4(m4);
        this.ringz_cone_geometry.applyMatrix4(m4);
        this.fence_tube_geometry.applyMatrix4(m4);

        this.ringxTrans_geometry.applyMatrix4(m4);
        this.ringyTrans_geometry.applyMatrix4(m4);
        this.ringzTrans_geometry.applyMatrix4(m4);
        this.axisx_rodTrans_geometry.applyMatrix4(m4);
        this.axisx_tipTrans_geometry.applyMatrix4(m4);
        this.axisy_rodTrans_geometry.applyMatrix4(m4);
        this.axisy_tipTrans_geometry.applyMatrix4(m4);
        this.axisz_rodTrans_geometry.applyMatrix4(m4);
        this.axisz_tipTrans_geometry.applyMatrix4(m4);
    }

    translate(t: THREE.Vector3) {
        // Translate manipulator by vector 't'

        // Translate main description vector
        this.center.addVectors(this.center, t);

        // Translate geometries
        this.cube_geometry.translate(t.x, t.y, t.z);
        this.ringx_geometry.translate(t.x, t.y, t.z);
        this.ringy_geometry.translate(t.x, t.y, t.z);
        this.ringz_geometry.translate(t.x, t.y, t.z);
        this.axisx_rod_geometry.translate(t.x, t.y, t.z);
        this.axisx_tip_geometry.translate(t.x, t.y, t.z);
        this.axisy_rod_geometry.translate(t.x, t.y, t.z);
        this.axisy_tip_geometry.translate(t.x, t.y, t.z);
        this.axisz_rod_geometry.translate(t.x, t.y, t.z);
        this.axisz_tip_geometry.translate(t.x, t.y, t.z);
        this.planexy_geometry.translate(t.x, t.y, t.z);
        this.planeyz_geometry.translate(t.x, t.y, t.z);
        this.planexz_geometry.translate(t.x, t.y, t.z);
        this.ringx_cone_geometry.translate(t.x, t.y, t.z);
        this.ringy_cone_geometry.translate(t.x, t.y, t.z);
        this.ringz_cone_geometry.translate(t.x, t.y, t.z);

        this.ringxTrans_geometry.translate(t.x, t.y, t.z);
        this.ringyTrans_geometry.translate(t.x, t.y, t.z);
        this.ringzTrans_geometry.translate(t.x, t.y, t.z);
        this.axisx_rodTrans_geometry.translate(t.x, t.y, t.z);
        this.axisx_tipTrans_geometry.translate(t.x, t.y, t.z);
        this.axisy_rodTrans_geometry.translate(t.x, t.y, t.z);
        this.axisy_tipTrans_geometry.translate(t.x, t.y, t.z);
        this.axisz_rodTrans_geometry.translate(t.x, t.y, t.z);
        this.axisz_tipTrans_geometry.translate(t.x, t.y, t.z);

        this.fence_tube_geometry.translate(t.x, t.y, t.z);
    }

    translate_to_ray(
        uid_manip_previous_raycaster_origin: THREE.Vector3,
        uid_manip_previous_raycaster_direction: THREE.Vector3,
        uid_manip_current_raycaster_origin: THREE.Vector3,
        uid_manip_current_raycaster_direction: THREE.Vector3,
    ) {
        this.translate(v3.sub(
            uid_manip_current_raycaster_origin,
            uid_manip_previous_raycaster_origin,
        ));
    }

    translate_along_edge(
        uid_manip_previous_raycaster_origin: THREE.Vector3,
        uid_manip_previous_raycaster_direction: THREE.Vector3,
        uid_manip_current_raycaster_origin: THREE.Vector3,
        uid_manip_current_raycaster_direction: THREE.Vector3,
    ) {
        const wv_parameter = new WrappedValue(NaN);
        // @ts-ignore
        const intersection_point = uil_get_ray_edge_intersection(
            this.rigid_move_edge_object_ids[0],
            uid_manip_current_raycaster_origin,
            uid_manip_current_raycaster_direction,
            wv_parameter
        );
        //console_log('Ray origin:', uid_manip_current_raycaster_origin);
        //console_log('Ray direction:', uid_manip_current_raycaster_direction);
        //console_log('intersection_point:', intersection_point);
        //console_log('Parameter value:', wv_parameter.v);
        if (wv_parameter.v > 0.1 && wv_parameter.v < 0.9) {
            //this.reattach_to_objects(true, wv_parameter.v);
            //var v0 = new THREE.Vector3();
            //v0.copy(this.center);
            //intersection_point.sub(v0);
            //this.translate(intersection_point);
            // @ts-ignore
            const d = uil_get_tangent_at_edge_param(this.rigid_move_edge_object_ids[0], wv_parameter.v).normalize();
            // @ts-ignore
            const objects_center = uil_get_point_at_edge_param(this.rigid_move_edge_object_ids[0], wv_parameter.v);
            // @ts-ignore
            const n = find_edge_normal(this.rigid_move_edge_object_ids[0], objects_center, uid_raycaster.ray.direction).normalize();
            this.match_to_vectors(intersection_point, d, n);
        }
    }

    rotate_around_axis(center: THREE.Vector3, axis: THREE.Vector3, angle: number) {
        const R = vectorRotation(axis, angle);

        if (isNaN(R.elements[0]!)) {
            //console_log(R);
        }

        const tocenter = new THREE.Vector3();
        const andback = new THREE.Vector3();
        tocenter.copy(center).multiplyScalar(-1);
        andback.copy(center);

        // Translate to origin
        this.translate(tocenter);

        // Rotate
        this.rotate(R);

        // Translate back
        this.translate(andback);
    }

    set_initial_hit_ray() {
        this.rigid_move_initial_hit_ray_origin = uid_raycaster.ray.origin;
        this.rigid_move_initial_hit_ray_direction = uid_raycaster.ray.direction;
    }

    reset_colors(): void {
        this.cube_material.color.set(rigid_colors.cube);
        this.ringx_material.color.set(rigid_colors.ringx);
        this.ringy_material.color.set(rigid_colors.ringy);
        this.ringz_material.color.set(rigid_colors.ringz);
        this.planexy_material.color.set(rigid_colors.planexy);
        this.planeyz_material.color.set(rigid_colors.planeyz);
        this.planexz_material.color.set(rigid_colors.planexz);
        this.axis_rodx_material.color.set(rigid_colors.rodx);
        this.axis_rody_material.color.set(rigid_colors.rody);
        this.axis_rodz_material.color.set(rigid_colors.rodz);
        this.axis_tip_material.color.set(rigid_colors.axis_tip);
    }

    static is_rigid_move_geometry(x: THREE.Mesh | undefined): boolean {
        return rigid_move_geometry_names.has(x?.geometry.name as any);
    }

    set_required_to_operate() {
        if (!App.state.rigid_move_manipulator)
            return;
        this.required_to_operate = true;
    }

    set_has_operated() {
        if (!App.state.rigid_move_manipulator)
            return;
        this.required_to_operate = false;
        this.has_already_operated = true;
    }

    has_operated() {
        if (!App.state.rigid_move_manipulator)
            return false;
        return this.has_already_operated;
    }

    is_required_to_operate() {
        if (!App.state.rigid_move_manipulator)
            return false;
        else
            return this.required_to_operate;
    }

}
